// @givmo/app-charity — the create-account email form (/sign-up/email).
//
// Its own route rather than a step inside the door, so browser Back means what a caller expects and
// the steps are linkable independently. This is where /sign-up's "Continue with Email" lands (the
// unified door's route-informed default — see auth-door.tsx); a returning caller who lands here is
// one click from the sign-in form via the footer, or — the definitive case — via the inline "Sign in
// instead" that appears when Firebase answers `emailAlreadyInUse`. Both carry the typed address
// through the email handoff, so recovery never means retyping.
//
// This screen creates the Firebase user (display name set), fires the backend OTP, and routes to
// /verify-email. The 3 live password-rule indicators mirror the admin sign-up. It carries EXACTLY ONE
// piece of guidance — the helper under Nonprofit email — and that placement is the point: the address
// typed there is half of what decides, two screens later, whether verification is instant or a
// document review (the other half, since D-CP-92, is whether Givmo has vetted the organization), and
// field helper text is where a caller looks for that. Everything else the sign-up card once said was
// removed by the 2026-08-11 operator ruling; do not reintroduce prose here.
//
// The FIELD reads "Nonprofit email" (matching the door subtitle's vocabulary) while the HELPER
// still says "your organization's work email". The helper is the rail-guarded sentence carrying the
// instant path's condition, and lives in `funnel-copy.ts`.
//
// ★ THE PERSONAL-EMAIL HARD BLOCK (CPA-WEB53, D-CP-63). The helper nudges; this screen also REFUSES.
// When the caller leaves the email field, and again on submit, the address is checked SERVER-SIDE
// through the backend's availability read (`personal-email-block.ts` — the same read the employer
// portal refuses on; this app carries no list). A blacklisted domain gets Jonathan's message verbatim
// as the field's error row, the submit is dead while the field holds that address, and the account
// is NEVER created: no Firebase user, no OTP, no `/verify-email`. The submit path checks too, so an
// autofill-and-Enter that never blurred the field cannot slip past. A nonprofit address proceeds
// exactly as before. The check fails OPEN (a transport failure answers "allowed"), because the claim
// wizard asks the same question again at its own door and the backend's Tier-1 engine refuses a
// personal domain regardless.
//
// No second-factor branch, deliberately: this path creates a brand-new account, which cannot owe a
// TOTP code. The providers — and with them the AS-WEB1 challenge — live on the door.
//
// A BOARD DIRECTOR ARRIVES HERE TOO (CBG-SEC2 SEC2-WEB, contract §4.3; mockup `m2-email-create`):
// "Continue with email" from a board invitation. An `arrival` prop rather than a copy. The director
// arrival renders in Board's director shell; its field reads "Email", and its one helper, when the
// invitation's facts came with it, says where the invitation went. ⚠ THE PERSONAL-EMAIL CHECK DOES
// NOT RUN FOR IT: a director's personal address must be accepted. That opens no path to a charity
// claim — the claim wizard refuses a personal address at its own door. After creating, it goes to the
// director's email confirmation. The console arrival is unchanged.

import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Button, Card, CardBody, TextField, ErrorMessage, testIdProp } from '@givmo/design-system';
import { DirectorShell } from '@givmo/board';
import { Check, X } from 'lucide-react';
import { useAuth } from '../../auth/auth-context';
import { AuthShell } from './auth-shell';
import { useAuthError } from './use-auth-error';
import { AUTH_ROUTES } from './auth-routes';
import {
  EMAIL_REGEX,
  signUpSchema,
  passwordHasMinLength,
  passwordHasLettersAndNumbers,
  passwordHasSymbol,
} from './auth-schemas';
import { stashEmailHandoff, takeEmailHandoff } from './email-handoff';
import {
  checkPersonalEmailDomain,
  normalizeEmail,
  type PersonalEmailVerdict,
} from './personal-email-block';
import { PERSONAL_EMAIL_BLOCK_MESSAGE, WORK_EMAIL_GUIDANCE } from '../../lib/funnel-copy';
import { sanitizeRedirectTarget } from '../../router/redirect-target';
import { takeDirectorInvitation } from '../board/director-invitation-handoff';
import { DIRECTOR_ROUTES } from '../board/director-routes';
import {
  DirectorAuthFooter,
  DIRECTOR_AUTH_LINK,
  DIRECTOR_AUTH_QUIET_LINK,
  directorEmailSearch,
} from './director-arrival';

// An UNMET rule is an instruction, not a disabled state: it stays at full legibility and the glyph
// carries the state, rather than the state being encoded as reduced contrast on the text.
function RuleRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className="flex items-center gap-8 font-sans text-s12 font-w400 tracking-givmo text-text-secondary">
      {ok ? (
        <Check size={14} className="text-success-600" aria-hidden />
      ) : (
        <X size={14} className="text-text-secondary" aria-hidden />
      )}
      <span>{label}</span>
    </li>
  );
}

export interface SignUpEmailScreenProps {
  /** Who arrived: the console's own sign-up (the default, unchanged) or a board director. */
  arrival?: 'console' | 'director';
}

export function SignUpEmailScreen({ arrival = 'console' }: SignUpEmailScreenProps = {}) {
  const director = arrival === 'director';
  const { signUpWithEmail, sendOtp } = useAuth();
  const navigate = useNavigate();
  // Two routes render this screen, so the search read is non-strict by necessity. `arrival` is
  // Board's, carried in from the director's sign-in form and back out to it (Board's R1).
  const { redirect, arrival: directorArrival } = useSearch({ strict: false }) as {
    redirect?: string;
    arrival?: 'another_account';
  };
  const { error, clearError, surface } = useAuthError();
  // The invitation's two facts, when a director came from one (taken once, per mount).
  const [invitation] = useState(() => (director ? takeDirectorInvitation() : null));

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // Prefilled when the caller crossed over from the sign-in form with an address already typed.
  const [email, setEmail] = useState(takeEmailHandoff);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // The one failure with a definitive remedy: Firebase's `emailAlreadyInUse` PROVES the account
  // exists, so the error gets an inline prefilled path to sign-in rather than a dead end.
  const [accountExists, setAccountExists] = useState(false);
  // The normalised address the backend last refused as a personal domain (CPA-WEB53). The refusal
  // is DERIVED against the field's current value below, so it stands exactly while the field holds
  // that address and lifts the moment a different one is typed — no clearing to forget.
  const [blockedEmail, setBlockedEmail] = useState<string | null>(null);
  // One verdict per address per screen instance: blur and submit both ask, and the second must not
  // spend a request the first already answered.
  const verdicts = useRef(new Map<string, PersonalEmailVerdict>());
  // A director's address is never refused: the check below doesn't run for them.
  const isBlocked = !director && blockedEmail !== null && normalizeEmail(email) === blockedEmail;

  /**
   * The door's question, asked of the backend: is this address's domain personal? Answers `allowed`
   * without a request for an address that is not yet well-formed (nothing to ask about) and for one
   * already answered. A `blocked` answer also raises the field's refusal.
   */
  const checkEmail = useCallback(async (candidate: string): Promise<PersonalEmailVerdict> => {
    if (!EMAIL_REGEX.test(candidate)) return 'allowed';
    const normalized = normalizeEmail(candidate);
    const known = verdicts.current.get(normalized);
    if (known !== undefined) {
      if (known === 'blocked') setBlockedEmail(normalized);
      return known;
    }
    const verdict = await checkPersonalEmailDomain(normalized);
    verdicts.current.set(normalized, verdict);
    if (verdict === 'blocked') setBlockedEmail(normalized);
    return verdict;
  }, []);

  const rules = useMemo(
    () => ({
      length: passwordHasMinLength(password),
      alnum: passwordHasLettersAndNumbers(password),
      symbol: passwordHasSymbol(password),
    }),
    [password],
  );

  const isValid = useMemo(
    () => signUpSchema.safeParse({ firstName, lastName, email, password }).success,
    [firstName, lastName, email, password],
  );

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!isValid || submitting || isBlocked) return;
      clearError();
      setAccountExists(false);
      setSubmitting(true);
      try {
        // The door's refusal, asked again here so the account is never created for a personal domain
        // the blur check did not get to see (an autofilled address submitted with Enter). Never for
        // a director, whose personal address must be accepted.
        if (!director && (await checkEmail(email)) === 'blocked') return;
        await signUpWithEmail({ email, password, firstName, lastName });
        // The new user is signed in; fire the OTP and route to verification. If the OTP send fails,
        // still route to /verify-email — that screen offers a resend.
        let otpSendFailed = false;
        try {
          await sendOtp();
        } catch {
          otpSendFailed = true;
        }
        // `otpSent`/`otpSendFailed` tell the verify screen a send already happened (or was tried),
        // so its no-prior-send auto-send stays off for this arrival.
        await navigate({
          to: director ? DIRECTOR_ROUTES.confirmEmail : AUTH_ROUTES.verifyEmail,
          search: {
            ...(redirect ? { redirect } : {}),
            ...(otpSendFailed ? { otpSendFailed: true } : { otpSent: true }),
          },
        });
      } catch (thrown) {
        setAccountExists(surface(thrown).kind === 'emailAlreadyInUse');
      } finally {
        setSubmitting(false);
      }
    },
    [
      isValid,
      submitting,
      isBlocked,
      checkEmail,
      clearError,
      signUpWithEmail,
      email,
      password,
      firstName,
      lastName,
      sendOtp,
      navigate,
      redirect,
      surface,
      director,
    ],
  );

  // The one exit both the footer and the already-registered recovery share: over to the sign-in
  // form, address in hand, and for a director with Board's arrival, so the lock notice there still
  // offers no organization door behind Board's "use another Givmo account" steps (Board's R1).
  const goToSignIn = useCallback(() => {
    stashEmailHandoff(email);
    void navigate(
      director
        ? {
            to: DIRECTOR_ROUTES.signInEmail,
            search: directorEmailSearch(redirect, directorArrival),
          }
        : { to: AUTH_ROUTES.signIn, search: redirect ? { redirect } : {} },
    );
  }, [email, navigate, redirect, director, directorArrival]);

  const emailField = director ? (
    <div className="flex flex-col gap-8">
      <TextField
        label="Email"
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        testId="auth-email-input"
      />
      {/* The one helper, at the field it is about: an address that matches the invitation's joins
          without a second code (D-CBG-17, "match or prove"); in a seat's move (CBG-SEC2-L4-MOVE,
          frame b3c), one that matches the seat's address moves it without one. */}
      {invitation !== null && invitation.purpose === 'seat_move' ? (
        <p
          className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-secondary"
          {...testIdProp('director-move-address-hint')}
        >
          {/* Frame b3c. */}
          Your seat’s address is {invitation.maskedEmailOfRecord}. Using that address here saves a
          step.
        </p>
      ) : invitation !== null ? (
        <p
          className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-secondary"
          {...testIdProp('director-invitation-address-hint')}
        >
          Your invitation went to {invitation.maskedEmailOfRecord}. Using that address here saves a
          step.
        </p>
      ) : null}
    </div>
  ) : (
    <div className="flex flex-col gap-8">
      {/* Leaving the field asks the backend whether the domain is personal (CPA-WEB53); a
          refused address renders Jonathan's message as the field's own error row
          (`auth-email-input-error`) and holds the submit until the address changes. */}
      <TextField
        label="Nonprofit email"
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onBlur={() => void checkEmail(email)}
        error={isBlocked}
        errorMessage={PERSONAL_EMAIL_BLOCK_MESSAGE}
        testId="auth-email-input"
      />
      {/* The single most consequential sentence in the funnel, and the only guidance on the
          screen: the address typed here is half of what decides, two screens later, whether
          verification is instant or a document review. */}
      <p
        className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-secondary"
        {...testIdProp('auth-work-email-hint')}
      >
        {WORK_EMAIL_GUIDANCE}
      </p>
    </div>
  );

  const form = (
    <form className="flex flex-col gap-16" onSubmit={(e) => void onSubmit(e)} noValidate>
      <div className="flex flex-col gap-16 sm:flex-row">
        <div className="flex-1">
          <TextField
            label="First name"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            testId="auth-first-name-input"
          />
        </div>
        <div className="flex-1">
          <TextField
            label="Last name"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            testId="auth-last-name-input"
          />
        </div>
      </div>

      {emailField}

      <TextField
        label="Password"
        type="password"
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        testId="auth-password-input"
      />

      <ul className="flex flex-col gap-6" {...testIdProp('auth-password-rules')}>
        <RuleRow ok={rules.length} label="At least 8 characters" />
        <RuleRow ok={rules.alnum} label="Contains letters and numbers" />
        <RuleRow ok={rules.symbol} label="Contains a symbol" />
      </ul>

      {error !== null && <ErrorMessage testId="auth-error">{error}</ErrorMessage>}
      {/* `emailAlreadyInUse` is the one failure that PROVES the remedy: the account exists, so the
            recovery is a prefilled sign-in, not a retype. Rendered with the error, not instead of it. */}
      {error !== null && accountExists && (
        <p className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
          <button
            type="button"
            onClick={goToSignIn}
            className="font-w600 text-brand-700 outline-none transition-colors hover:text-brand-800 focus-visible:underline motion-reduce:transition-none"
            {...testIdProp('auth-signin-instead')}
          >
            Sign in instead
          </button>
        </p>
      )}

      <Button
        type="submit"
        appearance="saas"
        tone="primary"
        size="lg"
        fullWidth
        disabled={!isValid || submitting || isBlocked}
        loading={submitting}
        testId="auth-signup-submit"
      >
        Create account
      </Button>
    </form>
  );

  if (director) {
    // `m2-email-create`. From an invitation, the way back is to it; from a seat's move (frame b3c),
    // to the move's own ways in, "Back to all options"; otherwise to the door.
    const target = sanitizeRedirectTarget(redirect);
    const fromInvitation = target?.startsWith(DIRECTOR_ROUTES.invitation) === true;
    const fromMove = target?.startsWith(DIRECTOR_ROUTES.move) === true;
    return (
      <DirectorShell
        title="Create your Givmo account"
        description={
          invitation?.purpose === 'seat_move'
            ? // Frame b3c.
              `Then your seat on ${
                invitation.organizationName === null
                  ? // The no-name fallback, approved in CBG-YR3-ROLES-COPY4.
                    'the board'
                  : `the ${invitation.organizationName} board`
              } moves to it.`
            : invitation?.organizationName
              ? `Then you’ll join the ${invitation.organizationName} board.`
              : undefined
        }
        testId="page-director-email-create"
      >
        <Card className="w-full max-w-[480px]" testId="director-email-create">
          <CardBody className="flex flex-col gap-20">
            {form}
            <DirectorAuthFooter>
              <span>
                Already have a Givmo account?{' '}
                <button
                  type="button"
                  onClick={goToSignIn}
                  className={DIRECTOR_AUTH_LINK}
                  {...testIdProp('auth-back-to-signin')}
                >
                  Sign in
                </button>
              </span>
              <button
                type="button"
                onClick={() =>
                  void navigate(
                    (fromInvitation || fromMove) && target !== undefined
                      ? { to: target }
                      : { to: DIRECTOR_ROUTES.signIn, search: redirect ? { redirect } : {} },
                  )
                }
                className={DIRECTOR_AUTH_QUIET_LINK}
                {...testIdProp('director-auth-back')}
              >
                {fromInvitation ? 'Back to the invitation' : 'Back to all options'}
              </button>
            </DirectorAuthFooter>
          </CardBody>
        </Card>
      </DirectorShell>
    );
  }

  return (
    <AuthShell testId="page-sign-up-email" wide>
      <div className="flex flex-col gap-4">
        <h1 className="font-sans text-s22 font-w600 tracking-givmo text-text-strong">
          Create your account
        </h1>
        <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
          Enter your details to create your login.
        </p>
      </div>

      {form}

      {/* The returning caller's way over, and the way back to the door. The sign-in link is the
          load-bearing one (this form is the /sign-up door's email default, so a returning caller can
          land here); the back link serves a caller who arrived by link or lost track — browser Back
          does it too, but not for them. */}
      <p className="flex flex-col gap-8 border-t border-border-subtle pt-20 text-center font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
        <span>
          Already have an account?{' '}
          <button
            type="button"
            onClick={goToSignIn}
            className="font-w600 text-brand-700 outline-none transition-colors hover:text-brand-800 focus-visible:underline motion-reduce:transition-none"
            {...testIdProp('auth-back-to-signin')}
          >
            Sign in
          </button>
        </span>
        <span>
          <button
            type="button"
            onClick={() =>
              void navigate({
                to: AUTH_ROUTES.signUp,
                search: redirect ? { redirect } : {},
              })
            }
            className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary outline-none transition-colors hover:text-text-strong focus-visible:underline motion-reduce:transition-none"
            {...testIdProp('auth-back-to-signup-options')}
          >
            Back to all options
          </button>
        </span>
      </p>
    </AuthShell>
  );
}
